import { useState, useEffect } from "react";
import { getLandingTeachers, balancedRows } from "@/utils/team";

const S_ICON =
  "https://customer-assets.emergentagent.com/job_superate-aprende/artifacts/jemdy22a_LogoVectorMorado_2.0_SinTexto.svg";

/* ── Card ─────────────────────────────────────────────── */

function TeamCard({ member }) {
  const [imgOk, setImgOk] = useState(false);
  const hasImage = Boolean(member.image);
  const imageSrc = member.image ? `/team/${member.image}` : "";

  useEffect(() => {
    if (!hasImage) return;
    const img = new Image();
    img.onload = () => setImgOk(true);
    img.onerror = () => setImgOk(false);
    img.src = imageSrc;
  }, [hasImage, imageSrc]);

  const showPhoto = hasImage && imgOk;
  const initials = member.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      data-testid={`team-card-${member.id}`}
      className="group w-full max-w-xs rounded-2xl bg-[#242429]/60 border border-white/5 overflow-hidden hover:border-[#9d4edd]/30 transition-colors duration-300"
    >
      {/* Header: photo or decorative fallback */}
      {showPhoto ? (
        <div className="h-56 overflow-hidden">
          <img
            src={imageSrc}
            alt={member.name}
            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
          />
        </div>
      ) : (
        <div className="h-56 flex items-center justify-center bg-[#1a1a1f] relative overflow-hidden">
          <img
            src={S_ICON}
            alt=""
            aria-hidden="true"
            className="absolute w-32 opacity-[0.04] pointer-events-none select-none"
          />
          <span className="text-5xl font-semibold tracking-tight text-[#9d4edd]/40 select-none relative">
            {initials}
          </span>
        </div>
      )}

      {/* Body */}
      <div className="p-5">
        <h3 className="text-base font-medium text-white mb-0.5">
          {member.name}
        </h3>
        <p className="text-sm text-[#9d4edd] mb-3">{member.title}</p>
        <p className="text-sm text-[#a1a1aa] leading-relaxed line-clamp-3">
          {member.bio}
        </p>
      </div>
    </div>
  );
}

/* ── Section ──────────────────────────────────────────── */

export default function TeamSection() {
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    fetch("/data/team.json")
      .then((r) => r.json())
      .then((data) => setTeachers(getLandingTeachers(data)))
      .catch(() => {});
  }, []);

  if (teachers.length === 0) return null;

  const rows = balancedRows(teachers, 3);

  return (
    <section
      id="equipo"
      data-testid="team-section"
      className="py-20 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-14">
          <p className="text-sm uppercase tracking-[0.2em] text-[#c77dff] font-semibold mb-4">
            Nuestro equipo
          </p>
          <h2
            data-testid="team-title"
            className="text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight text-white mb-4"
          >
            Quiénes te van a enseñar
          </h2>
          <p className="text-base text-[#a1a1aa] leading-relaxed">
            Profesores con experiencia real en los ramos más exigentes de
            ingeniería. Saben lo que necesitas porque ya pasaron por lo mismo.
          </p>
        </div>

        {/* Balanced grid */}
        <div className="flex flex-col gap-5">
          {rows.map((row, ri) => (
            <div
              key={ri}
              className="flex flex-col sm:flex-row gap-5 justify-center items-center sm:items-stretch"
            >
              {row.map((m) => (
                <TeamCard key={m.id} member={m} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
